import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
} from 'common/utility/constants';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';
import SrCard, {
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import { SR_TAG_THEME_GREEN_TEXT, SR_TAG_THEME_ORANGE_TEXT } from 'client/components/SrTag/SrTag';
import {
  SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
  SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP_INVERTED,
} from 'client/components/SrInfoOptionCheckbox/SrInfoOptionCheckbox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import UPTenantLiabilityBannerRow from 'client/components/UPTenantLiabilityBanner/UPTenantLiabilityBannerRow';
import UPUtilityConfirmationEmailForm from 'client/components/UPUtilityConfirmationEmailForm/UPUtilityConfirmationEmailForm';
import {
  isHomebuyerPartner,
  isTenantLiabilityEnabled,
  normalizeAddonsEnabled,
  shouldViewTenantLiabilityBanners,
} from 'common/utility/helpers';

const getFormFields = ({
  values,
  utilityTypes,
  proofOfPurchaseUtilityTypes,
  onToggleInternetPlansDiscountEnabled,
  onToggleAddonsEnabled,
  utilityPartner,
  onEnableTenantLiabilityClick,
}) => {
  return [
    {
      id: 'logoUrl',
      title: 'Company Logo',
      type: 'file_single',
      properties: {
        placeholder: 'Tap to Upload Logo',
        fileAccept: 'image/*',
        filePath: '/partner-uploads/logos/{uuid}',
      },
    },
    {
      id: 'customerSupportEmail',
      type: 'email',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Your Client Support: Email'
        : 'Your Tenant Support: Email',
      properties: {
        placeholder: 'Enter email address',
      },
      validations: ['email'],
    },
    {
      id: 'customerSupportPhone',
      type: 'phone',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Your Client Support: Phone'
        : 'Your Tenant Support: Phone',
      properties: {
        placeholder: 'Enter phone number',
      },
      validations: ['phone'],
    },
    {
      id: 'utilityConfirmationEmail',
      type: 'email',
      properties: {
        placeholder: 'name@company.com',
      },
      validations: ['email'],
    },
    {
      id: 'requiredUtilityTypeIds',
      type: 'info_checkbox_multi',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Default Settings for Clients'
        : 'Default Settings for Tenants',
      subtitle: shouldViewTenantLiabilityBanners(utilityPartner) ? (
        <SrBox marginBottom="-7px">
          <UPTenantLiabilityBannerRow
            compact
            utilityPartner={utilityPartner}
            onEnableClick={onEnableTenantLiabilityClick}
          />
        </SrBox>
      ) : null,
      properties: {
        choices: utilityTypes.map(({ id: utilityTypeId, label, iconUrl }) => ({
          label: (
            <SrFlex align="center" columnGap="5px">
              {utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label}
              <SrBox
                width="20px"
                height="20px"
                backgroundImage={`url("${iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
            </SrFlex>
          ),
          tagContents: values?.requiredUtilityTypeIds?.includes(utilityTypeId)
            ? 'Enabled'
            : 'Disabled',
          tagTheme: !values?.requiredUtilityTypeIds?.includes(utilityTypeId)
            ? SR_TAG_THEME_ORANGE_TEXT
            : SR_TAG_THEME_GREEN_TEXT,
          value: utilityTypeId,
          theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
        })),
      },
    },
    {
      id: 'internetPlansDiscountEnabled',
      type: 'info_checkbox_multi',
      properties: {
        handleDuringChange: onToggleInternetPlansDiscountEnabled,
        choices: [
          {
            label: 'Yes, offer discounts',
            value: true,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
            selected: true,
          },
          {
            label: 'No, do not turn on this program',
            value: false,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
          },
        ],
      },
    },
    {
      id: 'addonsEnabled',
      type: 'info_checkbox_multi',
      properties: {
        handleDuringChange: onToggleAddonsEnabled,
        choices: [
          {
            label: 'Yes, show add-ons',
            value: true,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
            selected: true,
          },
          {
            label: 'No, do not show add-ons',
            value: false,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
          },
        ],
      },
    },
    {
      id: 'proofOfPurchaseRequiredUtilityTypeIds',
      type: 'info_checkbox_multi',
      title: 'Proof of Purchase required for:',
      properties: {
        choices: proofOfPurchaseUtilityTypes.map(({ id: utilityTypeId, label, iconUrl }) => ({
          label: (
            <SrFlex align="center" columnGap="5px">
              {utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label}
              <SrBox
                width="20px"
                height="20px"
                backgroundImage={`url("${iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
            </SrFlex>
          ),
          tagContents: values?.proofOfPurchaseRequiredUtilityTypeIds?.includes(utilityTypeId)
            ? 'Proof required'
            : 'Proof not required',
          tagTheme: values?.proofOfPurchaseRequiredUtilityTypeIds?.includes(utilityTypeId)
            ? SR_TAG_THEME_ORANGE_TEXT
            : SR_TAG_THEME_GREEN_TEXT,
          value: utilityTypeId,
          theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP_INVERTED,
        })),
      },
    },
  ];
};

const SrUtilityPartnerSettingsTenantForm = ({
  utilityTypes,
  utilityPartner,
  onSubmitForm,
  onEnableTenantLiabilityClick,
}) => {
  const tenantLiabilityEnabled = isTenantLiabilityEnabled(utilityPartner);

  // Filter for "Default Settings for Tenants" - shows liability insurance when the partner
  // offers it (SimpleCover or an exclusive partner referral link)
  const filteredUtilityTypes = utilityTypes.filter(
    ({ id }) =>
      id !== UTILITY_TYPE_ID_INTERNET &&
      (id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE || tenantLiabilityEnabled),
  );

  // Filter for "Proof of Purchase" - always excludes liability insurance and internet
  const proofOfPurchaseUtilityTypes = utilityTypes.filter(
    ({ id }) => id !== UTILITY_TYPE_ID_INTERNET && id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  );
  const partnerUtilityTypes = utilityPartner?.requiredUtilityTypes || [];
  const partnerProofOfPurchaseRequiredUtilityTypes =
    utilityPartner?.proofOfPurchaseRequiredUtilityTypes || [];

  const initialValues = {
    logoUrl: utilityPartner?.logoUrl,
    requiredUtilityTypeIds: partnerUtilityTypes.map(({ utilityTypeId }) => utilityTypeId),
    proofOfPurchaseRequiredUtilityTypeIds: partnerProofOfPurchaseRequiredUtilityTypes.map(
      ({ utilityTypeId }) => utilityTypeId,
    ),
    customerSupportPhone: utilityPartner?.customerSupportPhone,
    customerSupportEmail: utilityPartner?.customerSupportEmail,
    utilityConfirmationEmail: utilityPartner?.utilityConfirmationEmail || '',
    internetPlansDiscountEnabled:
      utilityPartner?.experienceConfiguration?.internetPlansDiscountEnabled,
    addonsEnabled: utilityPartner?.experienceConfiguration?.addonsEnabled,
  };

  return (
    <SrBox className="SrUtilityPartnerSettingsTenantForm">
      <Formik
        enableReinitialize
        onSubmit={onSubmitForm}
        initialValues={{
          ...createInitialValues({
            formFields: getFormFields({
              values: initialValues,
              utilityTypes: filteredUtilityTypes,
              proofOfPurchaseUtilityTypes,
              utilityPartner,
              onEnableTenantLiabilityClick,
            }),
            defaultValues: initialValues,
          }),
          // createInitialValues() has no "unset" representation and falls back to '',
          // which would make an untouched add-ons toggle look like a chosen value on
          // submit. Keep the tri-state null so nothing is saved until someone picks.
          addonsEnabled: normalizeAddonsEnabled(initialValues.addonsEnabled),
        }}
        validate={createFormValidationFn(
          getFormFields({
            values: initialValues,
            utilityTypes: filteredUtilityTypes,
            proofOfPurchaseUtilityTypes,
            utilityPartner,
            onEnableTenantLiabilityClick,
          }),
        )}
      >
        {(formikBag) => {
          const formFields = getFormFields({
            values: formikBag.values,
            utilityTypes: filteredUtilityTypes,
            proofOfPurchaseUtilityTypes,
            onToggleInternetPlansDiscountEnabled: (newValue) => {
              formikBag.setFieldValue('internetPlansDiscountEnabled', newValue);
            },
            onToggleAddonsEnabled: (newValue) => {
              formikBag.setFieldValue('addonsEnabled', newValue);
            },
            utilityPartner,
            onEnableTenantLiabilityClick,
          });
          const getFormFieldsById = createFormFieldsByIdGetter(formFields);
          return (
            <SrBox marginBottom="20px">
              <Form>
                <SrCard
                  color={SR_CARD_BULLET_COLOR_PURPLE}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header={
                    isHomebuyerPartner(utilityPartner)
                      ? 'Brand the client experience'
                      : 'Brand the tenant experience'
                  }
                  subheader={
                    isHomebuyerPartner(utilityPartner)
                      ? 'Clients will see your logo and company name when they set up their utilities.'
                      : 'Tenants will see your logo and company name when they set up their utilities.'
                  }
                >
                  <SrBox width="600px" marginTop="12px" marginLeft="35px">
                    <SrFormikField
                      formField={getFormFieldsById('logoUrl')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrBox>
                </SrCard>
                <SrCard
                  color={SR_CARD_BULLET_COLOR_PURPLE}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header={
                    isHomebuyerPartner(utilityPartner)
                      ? 'Client Support Info'
                      : 'Tenant Support Info'
                  }
                  subheader={
                    isHomebuyerPartner(utilityPartner)
                      ? "If clients ask homebuyer questions to the Utility Wallet support team, we'll share your contact info below with them."
                      : "If tenants ask leasing questions to the Utility Profit support team, we'll share your contact info below with them."
                  }
                >
                  <SrBox width="600px" marginTop="12px" marginLeft="35px">
                    <SrFormikField
                      formField={getFormFieldsById('customerSupportPhone')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('customerSupportEmail')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrBox>
                </SrCard>
                <UPUtilityConfirmationEmailForm
                  formikBag={formikBag}
                  formField={getFormFieldsById('utilityConfirmationEmail')}
                />
                <SrCard
                  color={SR_CARD_BULLET_COLOR_PURPLE}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header="Enable or Disable Utilities By Default"
                  subheader={
                    'Choose whether this utility type is enabled or disabled by default for future synced listings. ' +
                    'This will not change existing listings.'
                  }
                >
                  <SrBox width="600px" marginTop="12px" marginLeft="35px">
                    <SrFormikField
                      formField={getFormFieldsById('requiredUtilityTypeIds')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrBox>
                </SrCard>
                {!isHomebuyerPartner(utilityPartner) && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_PURPLE}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header={
                      <>
                        Give tenants option for discount <br /> on high speed internet
                      </>
                    }
                    subheader={
                      <>
                        Tenants will be able to purchase internet at eligible homes through select
                        providers.
                        <br />
                        Utility Profit will bill the tenant directly. If tenants contact a
                        participating provider,
                        <br />
                        they'll be directed to activate service under this program, through Utility
                        Profit.
                      </>
                    }
                  >
                    <SrBox width="600px" marginTop="12px" marginLeft="35px">
                      <SrFormikField
                        formField={getFormFieldsById('internetPlansDiscountEnabled')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                      <SrBox marginTop="18px">
                        <SrText font="greycliff" weight="700" size="18">
                          By enrolling,{' '}
                          <SrLink
                            to="https://utilityprofit.com/internet-plans-discount"
                            color="blue300"
                            target="_blank"
                          >
                            I agree to the terms of service.
                          </SrLink>
                        </SrText>
                      </SrBox>
                    </SrBox>
                  </SrCard>
                )}

                {!isHomebuyerPartner(utilityPartner) && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_PURPLE}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header={
                      <>
                        Allow Tenants to Join <br /> Add-on Waitlists
                      </>
                    }
                    subheader={
                      <>
                        Tenants will see Home Security and Moving Service add-ons during setup and
                        can join the waitlist to register their interest. No one is charged or
                        enrolled during the waitlist period.
                      </>
                    }
                  >
                    <SrBox width="600px" marginTop="12px" marginLeft="35px">
                      <SrFormikField
                        formField={getFormFieldsById('addonsEnabled')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrBox>
                  </SrCard>
                )}

                {!isHomebuyerPartner(utilityPartner) && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_PURPLE}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header="Require Proof of Purchase"
                    subheader={
                      <>
                        Turning this on adds an extra step for renters and requires them to upload a
                        screenshot or confirmation as proof of purchase.&nbsp;
                        <SrLink
                          to="https://utilityprofit.frontkb.com/en/articles/3922753"
                          color="blue300"
                        >
                          <u>Learn more about proof here.</u>
                        </SrLink>
                      </>
                    }
                  >
                    <SrBox width="600px" marginTop="12px" marginLeft="35px">
                      <SrFormikField
                        formField={getFormFieldsById('proofOfPurchaseRequiredUtilityTypeIds')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrBox>
                  </SrCard>
                )}

                <SrStaticFooter>
                  <div />
                  <SrFooterSaveButton isSubmitting={false} />
                </SrStaticFooter>
              </Form>
            </SrBox>
          );
        }}
      </Formik>
    </SrBox>
  );
};

SrUtilityPartnerSettingsTenantForm.displayName = 'SrUtilityPartnerSettingsTenantForm';
SrUtilityPartnerSettingsTenantForm.propTypes = {
  utilityTypes: PropTypes.array.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  onEnableTenantLiabilityClick: PropTypes.func,
};

export default SrUtilityPartnerSettingsTenantForm;
